import {inject, Injectable, signal} from '@angular/core';
import {ColumnGroupToggleService} from "./column-group-toggle.service";
import {StickyColumnService} from "./sticky-column.service";
import {reorderColumnsAccordingToOrderProperty} from "../../components/records-table-view/configs/records-table-config";
import {RecordTableColumnParams, ColumnsForToggling} from "../../types/records-table.types";
import {ColumnParamsState} from "../../types/table-types/column-params-state.types";
import {UpdateHiddenColumnParams} from "../../types/table-types/hidden-column.types";
import {HiddenColumnService} from "./hidden-column.service";
import {ActiveColumnProfileService} from "./active-column-profile.service";
import {ToggleGroupAllVisibilityParams} from "../../types/table-types/toggle-group-all-visibility.types";


@Injectable({
  providedIn: 'root'
})
export class ColumnParamsStateService {
  private columnGroupToggleService: ColumnGroupToggleService = inject(ColumnGroupToggleService);
  private stickyColumnService: StickyColumnService = inject(StickyColumnService);
  private hiddenColumnService: HiddenColumnService = inject(HiddenColumnService);
  private activeColumnProfileService: ActiveColumnProfileService = inject(ActiveColumnProfileService);
  private _columnParamsState = signal<Array<ColumnParamsState>>([]);
  public columnParamsState = this._columnParamsState.asReadonly();

  public setColumnParamsState(key: string, columnsParams: Array<RecordTableColumnParams>): void {
    this._columnParamsState.update(currentState => {
      const existingIndex = currentState.findIndex(state => state.key === key);

      if (existingIndex >= 0) {
        const newState = [...currentState];
        newState[existingIndex] = { key, columnsParams };
        return newState;
      } else {
        return [...currentState, { key, columnsParams }];
      }
    });
  }

  public updateSticky(key: string, columnParams: RecordTableColumnParams): void {
    this._columnParamsState.update(currentState => {
      const stateIndex = currentState.findIndex(state => state.key === key);
      if (stateIndex < 0) { return currentState; }

      const state = currentState[stateIndex];
      const newColumns = this.stickyColumnService.toggleColumnStick({
        column: columnParams,
        allColumns: state.columnsParams,
        tableId: key
      });

      const updatedState = [...currentState];
      updatedState[stateIndex] = {
        ...state,
        columnsParams: reorderColumnsAccordingToOrderProperty(newColumns)
      };

      return updatedState;
    });
  }

  public updateHiddenGroup(key: string, columnsForToggling: ColumnsForToggling): void {
    this._columnParamsState.update(currentState => {
      const stateIndex = currentState.findIndex(state => state.key === key);
      if (stateIndex < 0) { return currentState; }

      const state = currentState[stateIndex];
      const newStateColumns = this.columnGroupToggleService.processHiddenGroups({
        currentColumns: state.columnsParams,
        columnsForToggling: columnsForToggling
      });

      if (newStateColumns === state.columnsParams) {
        return currentState;
      }

      const updatedState = [...currentState];
      updatedState[stateIndex] = {
        ...state,
        columnsParams: newStateColumns
      };
      return updatedState;
    });
  }

  public updateAllHiddenGroups({ key, action }: ToggleGroupAllVisibilityParams): void {
    this._columnParamsState.update(currentState => {
      const stateIndex = currentState.findIndex(state => state.key === key);
      if (stateIndex < 0) { return currentState; }

      const state = currentState[stateIndex];
      const columns = state.columnsParams;

      switch (action) {
        case 'EXPAND_GROUP':
          columns.forEach(column => {
            if (column.hiddenGroup) {
              column.hiddenGroup = false;
              column.hiddenGroupPlaceholder = false;
            }
          })
         break;
        case 'COLLAPSE_GROUP':
          const groupFirstVisible = new Map<string | number, RecordTableColumnParams>();
          columns.forEach(column => {
            const groupId = column.columnProperties.GROUP?.id;

            if (groupId) {
              if (!column.hiddenGroup) {
                column.hiddenGroup = true;
                column.hiddenGroupPlaceholder = false;
              }

              if (!groupFirstVisible.has(groupId) && !column.hiddenColumn && !column.sticky) {
                groupFirstVisible.set(groupId, column);
              }
            }
          });

          groupFirstVisible.forEach(targetColumn => {
            targetColumn.hiddenGroupPlaceholder = true;
          });

          break;
      }

      const updatedState = [...currentState];
      updatedState[stateIndex] = { ...state };

      return updatedState;
    });
  }

  public updateHiddenColumn(updateHiddenColumnParams: UpdateHiddenColumnParams): void {
    const { columnKeys, key, profileName } = updateHiddenColumnParams;
    this._columnParamsState.update(currentState => {
      const stateIndex = currentState.findIndex(state => state.key === key);
      if (stateIndex < 0) { return currentState; }

      const state = currentState[stateIndex];
      const updatedColumns = this.hiddenColumnService.toggleMultipleColumnsVisibility({
        columnKeys: columnKeys,
        columns: state.columnsParams,
        profileName: profileName
      });

      const updatedState = [...currentState];
      updatedState[stateIndex] = {
        ...state,
        columnsParams: updatedColumns
      };

      return updatedState;
    });
  }

  public updateActiveProfile(updateHiddenColumnParams: UpdateHiddenColumnParams): void {
    const { key, profileName } = updateHiddenColumnParams;
    if (!profileName) { return; }

    const activeProfile = this.activeColumnProfileService.updateActiveColumnProfile(profileName);
    this._columnParamsState.update(currentState => {
      const stateIndex = currentState.findIndex(state => state.key === key);
      if (stateIndex < 0) { return currentState; }

      const state = currentState[stateIndex];
      const hiddenKeys = activeProfile?.hiddenColumnKeys ?? [];
      const updatedColumns = this.hiddenColumnService.applyColumnProfileVisibility({
        columnKeys: hiddenKeys,
        columns: state.columnsParams,
        profileName
      });

      const updatedState = [...currentState];
      updatedState[stateIndex] = {
        ...state,
        columnsParams: updatedColumns
      };

      return updatedState;
    });
  }

  public isNeedGroup(key: string): boolean {
    const state = this._columnParamsState().find(state => state.key === key);
    return state ? state.columnsParams.some(i => i.columnProperties.GROUP?.id) : false;
  }

  public getColumnParamsByKey(key: string): Array<RecordTableColumnParams> | undefined {
    return this.columnParamsState().find(state => state.key === key)?.columnsParams.filter(i => !i.hiddenColumn);
  }

  public getAllColumnParamsByKey(key: string): Array<RecordTableColumnParams> | undefined {
    return this.columnParamsState().find(state => state.key === key)?.columnsParams;
  }

  public resetTableColumnParamsState(key?: string): void {
    key ?
      this._columnParamsState.update(currentState => currentState.filter(state => state.key !== key)) :
      this._columnParamsState.set([]);
  }
}
